import { db } from '../../db/database';
import { notifyOutboxChanged } from '../../services/sync/syncEngine';
import { customerCode, normalizePhilippineMobile } from './customerUtils';
import type { CustomerRecord } from '../../db/types';

export type CustomerInput = {
  name: string;
  mobileNumber?: string;
  address?: string;
  notes?: string;
};

export async function createCustomer(
  input: CustomerInput,
  actorId = 'cashier-local',
  deviceId = 'device-local',
): Promise<CustomerRecord> {
  const name = input.name.trim();
  if (!name) throw new Error('Customer name is required.');
  const now = new Date().toISOString();
  const customer: CustomerRecord = {
    id: crypto.randomUUID(),
    customerCode: customerCode(),
    name,
    mobileNumber: normalizePhilippineMobile(input.mobileNumber),
    address: input.address?.trim() || undefined,
    notes: input.notes?.trim() || undefined,
    createdAt: now,
    updatedAt: now,
  };
  await db.transaction('rw', [db.customers, db.auditLogs, db.outbox], async () => {
    await db.customers.add(customer);
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'CUSTOMER_CREATED',
      entityType: 'CUSTOMER',
      entityId: customer.id,
      actorId,
      deviceId,
      metadata: JSON.stringify({ customerCode: customer.customerCode }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'CUSTOMER_CREATED',
      aggregateType: 'CUSTOMER',
      aggregateId: customer.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify(customer),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
  return customer;
}

export async function updateCustomer(
  id: string,
  input: CustomerInput,
  actorId = 'cashier-local',
  deviceId = 'device-local',
): Promise<CustomerRecord> {
  const existing = await db.customers.get(id);
  if (!existing) throw new Error('Customer not found.');
  const name = input.name.trim();
  if (!name) throw new Error('Customer name is required.');
  const now = new Date().toISOString();
  const updated: CustomerRecord = {
    ...existing,
    name,
    mobileNumber: normalizePhilippineMobile(input.mobileNumber),
    address: input.address?.trim() || undefined,
    notes: input.notes?.trim() || undefined,
    updatedAt: now,
  };
  await db.transaction('rw', [db.customers, db.auditLogs, db.outbox], async () => {
    await db.customers.put(updated);
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'CUSTOMER_UPDATED',
      entityType: 'CUSTOMER',
      entityId: id,
      actorId,
      deviceId,
      metadata: JSON.stringify({ customerCode: existing.customerCode }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'CUSTOMER_UPDATED',
      aggregateType: 'CUSTOMER',
      aggregateId: id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify(updated),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
  return updated;
}
